import React from 'react';
import {useControllerProps} from '../../../../../../../domain/controllers/ControllerContext';
import {DestinationPicker} from '../DestinationPicker/DestinationPicker';
import {useTranslation} from '@wix/yoshi-flow-editor';
import type {TFunction} from '@wix/wix-i18n-config';
import orderSummaryStyle from '../../OrderSummary.scss';
import classNames from 'clsx';
import {TotalUpdateSkeleton} from '../TotalUpdateSkeleton';
import {DiscountScope, DiscountSource} from '../../../../../../../domain/models/CartSummary/Discount.model';

export enum TotalShippingDataHook {
  Root = 'TotalShipping.Root',
  TitleRoot = 'TotalShipping.TitleRoot',
  Title = 'TotalShipping.Title',
  Value = 'TotalShipping.Value',
  Picker = 'ShippingDestination.Picker',
}

export const TotalShipping = () => {
  const {t} = useTranslation();
  const {shouldDisplayDeliveryError, isPickup} = useControllerProps().orderStore;
  const {cartSummary, cartModel, isSummaryUpdating, couponAlignmentInCartAndCheckout} = useControllerProps().cartStore;
  const hasDeliveryAddress = Boolean(cartModel.deliveryInfo?.address.country);
  const isFreeShippingCoupon = couponAlignmentInCartAndCheckout
    ? cartSummary?.globalDiscounts.some(
        (discount) => discount.scope === DiscountScope.DELIVERY && discount.sourceType === DiscountSource.COUPON
      )
    : cartSummary?.couponDiscount?.isFreeShippingCoupon;

  const shouldDisplayNoAmount = !cartSummary?.deliverySummary?.method;

  const getShippingRowValueText = (): string | React.ReactNode => {
    if (isSummaryUpdating) {
      return <TotalUpdateSkeleton />;
    }
    if (shouldDisplayNoAmount) {
      return t('cart.summary.shipping.noAmount');
    }
    if (cartSummary.priceSummary.delivery.convertedAmount === 0 || isFreeShippingCoupon) {
      return t('cart.free_shipping');
    }
    return cartSummary.priceSummary.delivery.formattedConvertedAmount;
  };

  const showPrice = !shouldDisplayDeliveryError && hasDeliveryAddress;
  return (
    <div data-hook={TotalShippingDataHook.Root}>
      <dl
        className={classNames(
          orderSummaryStyle.deliveryLine,
          !hasDeliveryAddress && orderSummaryStyle.destinationPickerOnly
        )}>
        <dt>
          <div data-hook={TotalShippingDataHook.TitleRoot}>
            {hasDeliveryAddress && (
              <span data-hook={TotalShippingDataHook.Title}>{getTotalShippingTitle({t, isPickup})}</span>
            )}
            <DestinationPicker dataHook={TotalShippingDataHook.Picker} />
          </div>
        </dt>

        {showPrice && (
          <dd
            data-hook={TotalShippingDataHook.Value}
            data-wix-total-shipping-estimated-shipping={TotalShippingDataHook.Value}>
            {getShippingRowValueText()}
          </dd>
        )}
      </dl>
    </div>
  );
};

function getTotalShippingTitle({t, isPickup}: {t: TFunction; isPickup: boolean}) {
  return isPickup ? t('cart.estimated_pickup') : t('cart.estimated_shipping');
}
